import {
    Box,
    Button,
    Center,
    Checkbox,
    Flex,
    Input,
    InputGroup,
    Menu,
    MenuButton,
    MenuItemOption,
    MenuList,
    MenuOptionGroup,
    Select,
    Stack,
    Text,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import usePipelineStore from '../../../stores/pipeline-store';
import ColorPicker from '../ColorPicker';
import { useCallback } from 'react';
import { ListReorderButtons } from '../ListReorderButtons';
import { DrawerInput, DrawerSelect, StepSelect, VarSelect } from './inputs';

type VarEqualTransitionSettingsProps = {
    transition: VarEqualTransition;
    setTransitionProp: any;
    pipeline: PipelineJson;
};
function VarEqualTransitionSettings({ transition, setTransitionProp, pipeline }: VarEqualTransitionSettingsProps) {
    return (
        <InputGroup>
            <VarSelect
                value={transition.varId}
                filterByType={['STRING', 'NUMBER', 'BOOLEAN']}
                onChange={(value) => setTransitionProp('varId', value)}
            />
            <Center mx={2}>=</Center>
            <Input
                backgroundColor="white"
                placeholder="value"
                // focusBorderColor={"white"}
                type={transition.varId && pipeline.vars[transition.varId]?.type === 'NUMBER' ? 'number' : 'text'}
                value={`${transition.value}`}
                onChange={({ target }: any) => {
                    setTransitionProp('value', target.value);
                }}
            />
        </InputGroup>
    );
}

type WaitAnswersTransitionSettingsProps = {
    transition: WaitAnswersTransition;
    setTransitionProp: any;
    pipeline: PipelineJson;
};
function WaitAnswersTransitionSettings({
    transition,
    setTransitionProp,
    pipeline,
}: WaitAnswersTransitionSettingsProps) {
    return (
        <InputGroup>
            <Stack>
                <Flex alignItems={'center'}>
                    <Text mr={2}>Storage:</Text>
                    <VarSelect
                        value={transition.responsesCounterTargetVarId}
                        placeholder="Survey Ref Responses Count Var"
                        filterByType={['NUMBER']}
                        onChange={(value) => setTransitionProp('responsesCounterTargetVarId', value)}
                    />
                </Flex>
                <Checkbox
                    minW={100}
                    pl={2}
                    isChecked={transition.amountIsVar}
                    onChange={({ target }: any) => {
                        setTransitionProp('amountIsVar', target.checked);
                    }}
                >
                    <Text fontSize={'xs'} minW={32}>
                        Use var for value
                    </Text>
                </Checkbox>
                <Flex alignItems={'center'}>
                    <Text mr={2} w={20}>
                        Amount:
                    </Text>
                    {transition.amountIsVar ? (
                        <VarSelect
                            value={transition.amountVarId}
                            filterByType={['NUMBER']}
                            onChange={(value) => setTransitionProp('amountVarId', value)}
                        />
                    ) : (
                        <Input
                            ml={2}
                            backgroundColor="white"
                            placeholder="Number of answers"
                            // focusBorderColor={"white"}
                            type={'number'}
                            value={transition.amount}
                            onChange={({ target }: any) => {
                                setTransitionProp('amount', target.value);
                            }}
                        />
                    )}
                </Flex>
            </Stack>
        </InputGroup>
    );
}

type WaitTimeTransitionSettingsProps = {
    transition: WaitTimeTransition;
    setTransitionProp: any;
    pipeline: PipelineJson;
};
function WaitTimeTransitionSettings({ transition, setTransitionProp, pipeline }: WaitTimeTransitionSettingsProps) {
    const inputPlaceHolder = `${transition.displayUnits} to wait`;
    let inputValue = transition.seconds;
    if (transition.displayUnits == 'minutes') {
        inputValue = transition.seconds / 60;
    } else if (transition.displayUnits == 'hours') {
        inputValue = transition.seconds / 60 / 60;
    } else if (transition.displayUnits == 'days') {
        inputValue = transition.seconds / 60 / 60 / 24;
    }
    return (
        <InputGroup>
            <Flex w="100%">
                <Checkbox
                    minW={140}
                    pl={2}
                    isChecked={transition.isFromVar}
                    onChange={({ target: { checked } }: any) => {
                        setTransitionProp('isFromVar', checked);
                    }}
                >
                    <Text fontSize={'xs'} minW={'62px'}>
                        Use var for value
                    </Text>
                </Checkbox>
                {transition.isFromVar ? (
                    <VarSelect
                        value={transition.varId}
                        placeholder="Var"
                        filterByType={['NUMBER']}
                        onChange={(value) => setTransitionProp('varId', value)}
                        width="100%"
                    />
                ) : (
                    <Input
                        textAlign={'center'}
                        backgroundColor="white"
                        placeholder={inputPlaceHolder}
                        // focusBorderColor={"white"}
                        type={'number'}
                        value={inputValue}
                        onChange={({ target }: any) => {
                            let seconds = target.value;
                            if (transition.displayUnits == 'minutes') {
                                seconds = target.value * 60;
                            } else if (transition.displayUnits == 'hours') {
                                seconds = target.value * 60 * 60;
                            } else if (transition.displayUnits == 'days') {
                                seconds = target.value * 60 * 60 * 24;
                            }
                            setTransitionProp('seconds', seconds);
                        }}
                    />
                )}

                <Select
                    ml={2}
                    minW={120}
                    value={transition.displayUnits}
                    onChange={({ target: { value } }: any) => {
                        setTransitionProp('displayUnits', value);
                    }}
                    w={'100%'}
                >
                    <option value="seconds">Seconds</option>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                </Select>
            </Flex>
        </InputGroup>
    );
}

type TimeWindowTransitionSettingsProps = {
    transition: TimeWindowTransition;
    setTransitionProp: any;
    pipeline: PipelineJson;
};
function TimeWindowTransitionSettings({ transition, setTransitionProp, pipeline }: TimeWindowTransitionSettingsProps) {
    //     startWindowTime: { hours: number; minutes: number };
    //     endWindowTime: { hours: number; minutes: number };

    return (
        <>
            <InputGroup>
                <Flex alignItems={'center'}>
                    <Text mr={2}>Timezone:</Text>
                    <Select
                        ml={2}
                        minW={120}
                        value={transition.timezone}
                        onChange={({ target: { value } }: any) => {
                            setTransitionProp('timezone', value);
                        }}
                    >
                        <option value="Asia/Jerusalem">Israel</option>
                    </Select>
                </Flex>
            </InputGroup>
            <InputGroup>
                <Flex alignItems={'center'}>
                    <Text mr={2}>Days:</Text>
                    <Menu closeOnSelect={false} variant={'outline'}>
                        <MenuButton w={'100'} as={Button}>
                            {transition.days.length} days selected
                        </MenuButton>
                        <MenuList minWidth="240px">
                            <MenuOptionGroup
                                title=""
                                type="checkbox"
                                value={transition.days}
                                onChange={(days: any) => {
                                    setTransitionProp('days', days);
                                }}
                            >
                                <MenuItemOption value="monday">Monday</MenuItemOption>
                                <MenuItemOption value="tuesday">Tuesday</MenuItemOption>
                                <MenuItemOption value="wednesday">Wednesday</MenuItemOption>
                                <MenuItemOption value="thursday">Thursday</MenuItemOption>
                                <MenuItemOption value="friday">Friday</MenuItemOption>
                                <MenuItemOption value="saturday">Saturday</MenuItemOption>
                                <MenuItemOption value="sunday">Sunday</MenuItemOption>
                            </MenuOptionGroup>
                        </MenuList>
                    </Menu>
                </Flex>
            </InputGroup>
            <InputGroup>
                <Flex alignItems={'center'}>
                    <Text mr={2} w={10}>
                        Start:
                    </Text>
                    <Input
                        value={transition.startWindowTimeHours}
                        w={55}
                        type={'number'}
                        onChange={({ target: { value } }: any) => {
                            setTransitionProp('startWindowTimeHours', value);
                        }}
                    />
                    :
                    <Input
                        value={transition.startWindowTimeMinutes}
                        w={55}
                        type={'number'}
                        onChange={({ target: { value } }: any) => {
                            setTransitionProp('startWindowTimeMinutes', value);
                        }}
                    />
                </Flex>
            </InputGroup>
            <InputGroup>
                <Flex alignItems={'center'}>
                    <Text mr={2} w={10}>
                        End:
                    </Text>
                    <Input
                        value={transition.endWindowTimeHours}
                        w={55}
                        type={'number'}
                        onChange={({ target: { value } }: any) => {
                            setTransitionProp('endWindowTimeHours', value);
                        }}
                    />
                    :
                    <Input
                        value={transition.endWindowTimeMinutes}
                        w={55}
                        type={'number'}
                        onChange={({ target: { value } }: any) => {
                            setTransitionProp('endWindowTimeMinutes', value);
                        }}
                    />
                </Flex>
            </InputGroup>
        </>
    );
}

type CycleInstancesDataTransitionSettingsProps = {
    transition: CycleInstancesDataTransition;
    setTransitionProp: any;
    pipeline: PipelineJson;
};
function CycleInstancesDataTransitionSettings({
    transition,
    setTransitionProp,
    pipeline,
}: CycleInstancesDataTransitionSettingsProps) {
    return (
        <InputGroup>
            <Input
                backgroundColor="white"
                placeholder="Instance data item in cycle state"
                // focusBorderColor={"white"}
                type={'text'}
                value={`${transition.instanceDataItem}`}
                onChange={({ target }: any) => {
                    setTransitionProp('instanceDataItem', target.value);
                }}
            />
            <Center mx={2}>=</Center>
            <Input
                backgroundColor="white"
                placeholder="value"
                // focusBorderColor={"white"}
                type={'text'}
                value={`${transition.value}`}
                onChange={({ target }: any) => {
                    setTransitionProp('value', target.value);
                }}
            />
        </InputGroup>
    );
}

type CycleInstancesDataNotEqualTransitionSettingsProps = {
    transition: CycleInstancesDataNotEqualTransition;
    setTransitionProp: any;
    pipeline: PipelineJson;
};
function CycleInstancesDataNotEqualTransitionSettings({
    transition,
    setTransitionProp,
    pipeline,
}: CycleInstancesDataNotEqualTransitionSettingsProps) {
    return (
        <InputGroup>
            <Input
                backgroundColor="white"
                placeholder="Instance data item in cycle state"
                // focusBorderColor={"white"}
                type={'text'}
                value={`${transition.instanceDataItem}`}
                onChange={({ target }: any) => {
                    setTransitionProp('instanceDataItem', target.value);
                }}
            />
            <Center mx={2}>{'!='}</Center>
            <Checkbox
                minW={140}
                pl={2}
                isChecked={transition.isFromVar}
                onChange={({ target: { checked } }: any) => {
                    setTransitionProp('isFromVar', checked);
                }}
            >
                <Text fontSize={'xs'} minW={'62px'}>
                    Use var for value
                </Text>
            </Checkbox>
            {transition.isFromVar ? (
                <VarSelect
                    value={transition.varId}
                    filterByType={['STRING', 'NUMBER']}
                    onChange={(value) => setTransitionProp('varId', value)}
                />
            ) : (
                <Input
                    backgroundColor="white"
                    placeholder="value"
                    // focusBorderColor={"white"}
                    type={'text'}
                    value={`${transition.value}`}
                    onChange={({ target }: any) => {
                        setTransitionProp('value', target.value);
                    }}
                />
            )}
        </InputGroup>
    );
}

type VarNotEqualTransitionSettingsProps = {
    transition: VarNotEqualTransition;
    setTransitionProp: any;
    pipeline: PipelineJson;
};

function VarNotEqualTransitionSettings({
    transition,
    setTransitionProp,
    pipeline,
}: VarNotEqualTransitionSettingsProps) {
    return (
        <InputGroup>
            <VarSelect
                value={transition.varId}
                filterByType={['STRING', 'NUMBER']}
                onChange={(value) => setTransitionProp('varId', value)}
            />
            <Center mx={2}>!=</Center>
            <Checkbox
                minW={140}
                pl={2}
                isChecked={transition.isFromVar}
                onChange={({ target: { checked } }: any) => {
                    setTransitionProp('isFromVar', checked);
                }}
            >
                <Text fontSize={'xs'} minW={'62px'}>
                    Use var for value
                </Text>
            </Checkbox>
            {transition?.isFromVar ? (
                <VarSelect
                    value={transition.value ?? ''}
                    filterByType={['STRING', 'NUMBER']}
                    onChange={(value) => setTransitionProp('value', value)}
                />
            ) : (
                <Input
                    backgroundColor="white"
                    placeholder="value"
                    type={transition.varId && pipeline.vars[transition.varId]?.type === 'NUMBER' ? 'number' : 'text'}
                    value={`${transition.value}`}
                    onChange={({ target }: any) => {
                        setTransitionProp('value', target.value);
                    }}
                />
            )}
        </InputGroup>
    );
}

type ExpirationTimeTransitionSettingsProps = {
    transition: ExpirationTimeTransition;
    setTransitionProp: any;
};

function ExpirationTimeTransitionSettings({
    transition,
    setTransitionProp,
}: ExpirationTimeTransitionSettingsProps) {
    return (
        <InputGroup>
            <VarSelect
                value={transition.varId}
                filterByType={['NUMBER']}
                onChange={(value) => setTransitionProp('varId', value)}
            />
        </InputGroup>
    );
}

type TransitionSettingsProps = {
    stepId: string;
    transitionIndex: number;
    onReorderTransition: (fromIndex: number, toIndex: number) => void;
};
function TransitionSettings({ stepId, transitionIndex, onReorderTransition }: TransitionSettingsProps) {
    const { pipeline, updateStep } = usePipelineStore((state) => state);
    const step = pipeline.steps[stepId] as PipelineStep;
    const transition = step.transitions[transitionIndex];

    const deleteTransition = useCallback(() => {
        const transitions = JSON.parse(JSON.stringify(step.transitions)) as Transition[];
        transitions.splice(transitionIndex, 1);
        updateStep(stepId, 'transitions', transitions);
    }, [step, stepId, transitionIndex, updateStep]);
    const setTransitionType = useCallback(
        ({ target: { value } }: any) => {
            let transitions = JSON.parse(JSON.stringify(step.transitions)) as Transition[];

            if (value == 'NONE') {
                const t: Transition = {
                    condition: 'NONE',
                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                };
                transitions[transitionIndex] = t;
            }

            if (value == 'VAR_EQUAL') {
                const t: VarEqualTransition = {
                    condition: 'VAR_EQUAL',
                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                    value: '',
                    varId: '',
                };
                transitions[transitionIndex] = t;
            }

            if (value == 'WAIT_ANSWERS') {
                const t: WaitAnswersTransition = {
                    condition: 'WAIT_ANSWERS',
                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                    responsesCounterTargetVarId: '',
                    amountIsVar: false,
                    amount: 1,
                    amountVarId: '',
                };
                transitions[transitionIndex] = t;
            }

            if (value == 'WAIT_TIME') {
                const t: WaitTimeTransition = {
                    condition: 'WAIT_TIME',
                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                    displayUnits: 'minutes',
                    seconds: 60,
                    isFromVar: false,
                    varId: '',
                };
                transitions[transitionIndex] = t;
            }

            if (value == 'TIME_WINDOW') {
                const t: TimeWindowTransition = {
                    condition: 'TIME_WINDOW',
                    timezone: 'Asia/Jerusalem',

                    days: [],

                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                    startWindowTimeHours: 10,
                    startWindowTimeMinutes: 30,
                    endWindowTimeHours: 12,
                    endWindowTimeMinutes: 30,
                };
                transitions[transitionIndex] = t;
            }

            if (value == 'CYCLE_INSTANCES_DATA') {
                const t: CycleInstancesDataTransition = {
                    condition: 'CYCLE_INSTANCES_DATA',
                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                    instanceDataItem: '',
                    value: '',
                };
                transitions[transitionIndex] = t;
            }

            if (value == 'CYCLE_INSTANCES_DATA_NOT_EQUAL') {
                const t: CycleInstancesDataNotEqualTransition = {
                    condition: 'CYCLE_INSTANCES_DATA_NOT_EQUAL',
                    isFromVar: false,
                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                    instanceDataItem: '',
                    value: '',
                    varId: '',
                };
                transitions[transitionIndex] = t;
            }

            if (value == 'VAR_NOT_EQUAL') {
                const t: VarNotEqualTransition = {
                    condition: 'VAR_NOT_EQUAL',
                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                    value: '',
                    varId: '',
                };
                transitions[transitionIndex] = t;
            }

            if (value == 'EXPIRATION_TIME') {
                const t: ExpirationTimeTransition = {
                    condition: 'EXPIRATION_TIME',
                    color: transition.color,
                    label: transition.label,
                    targetStepId: transition.targetStepId,
                    varId: '',
                };
                transitions[transitionIndex] = t;
            }

            updateStep(stepId, 'transitions', transitions);
        },
        [step]
    );

    const setTransitionProp = useCallback(
        (propName, propValue) => {
            let transitions = JSON.parse(JSON.stringify(step.transitions)) as any;
            transitions[transitionIndex][propName] = propValue;
            updateStep(stepId, 'transitions', transitions);
        },
        [pipeline, updateStep]
    );

    return (
        <Box borderWidth="1px" borderRadius="md" padding={2}>
            <Stack>
                <Box>
                    <InputGroup>
                        <ListReorderButtons
                            index={transitionIndex}
                            itemCount={step.transitions.length}
                            onReorder={onReorderTransition}
                        />
                        <StepSelect
                            value={transition.targetStepId}
                            excludeStepId={stepId}
                            placeholder="Select Step"
                            onChange={(value) => setTransitionProp('targetStepId', value)}
                        />
                        <Button
                            w={0}
                            backgroundColor="white"
                            _hover={{ color: '#f0706e' }}
                            _focus={{ backgroundColor: 'white' }}
                            _active={{ backgroundColor: 'white' }}
                            color={'grey'}
                            leftIcon={<DeleteIcon />}
                            onClick={deleteTransition}
                        />
                    </InputGroup>
                </Box>
                <Box>
                    <InputGroup>
                        <DrawerSelect value={transition.condition} onChange={setTransitionType}>
                            <option value="NONE">No Condition</option>
                            <option value="VAR_EQUAL">Var value is equal</option>
                            <option value="WAIT_ANSWERS">Wait for answers</option>
                            <option value="WAIT_TIME">Wait time</option>
                            <option value="TIME_WINDOW">Time window</option>
                            <option value="CYCLE_INSTANCES_DATA">Cycle state - instances data is equal</option>
                            <option value="CYCLE_INSTANCES_DATA_NOT_EQUAL">
                                Cycle state - instances data is not equal
                            </option>
                            <option value="VAR_NOT_EQUAL">Var value is not equal</option>
                            <option value="EXPIRATION_TIME">Expiration time</option>
                        </DrawerSelect>
                    </InputGroup>
                </Box>

                {transition.condition == 'VAR_EQUAL' && (
                    <VarEqualTransitionSettings
                        pipeline={pipeline}
                        setTransitionProp={setTransitionProp}
                        transition={transition as VarEqualTransition}
                    />
                )}
                {transition.condition == 'WAIT_ANSWERS' && (
                    <WaitAnswersTransitionSettings
                        pipeline={pipeline}
                        setTransitionProp={setTransitionProp}
                        transition={transition as WaitAnswersTransition}
                    />
                )}

                {transition.condition == 'WAIT_TIME' && (
                    <WaitTimeTransitionSettings
                        pipeline={pipeline}
                        setTransitionProp={setTransitionProp}
                        transition={transition as WaitTimeTransition}
                    />
                )}

                {transition.condition == 'TIME_WINDOW' && (
                    <TimeWindowTransitionSettings
                        pipeline={pipeline}
                        setTransitionProp={setTransitionProp}
                        transition={transition as TimeWindowTransition}
                    />
                )}

                {transition.condition == 'CYCLE_INSTANCES_DATA' && (
                    <CycleInstancesDataTransitionSettings
                        pipeline={pipeline}
                        setTransitionProp={setTransitionProp}
                        transition={transition as CycleInstancesDataTransition}
                    />
                )}

                {transition.condition == 'CYCLE_INSTANCES_DATA_NOT_EQUAL' && (
                    <CycleInstancesDataNotEqualTransitionSettings
                        pipeline={pipeline}
                        setTransitionProp={setTransitionProp}
                        transition={transition as CycleInstancesDataNotEqualTransition}
                    />
                )}

                {transition.condition == 'VAR_NOT_EQUAL' && (
                    <VarNotEqualTransitionSettings
                        pipeline={pipeline}
                        setTransitionProp={setTransitionProp}
                        transition={transition as VarNotEqualTransition}
                    />
                )}

                {transition.condition == 'EXPIRATION_TIME' && (
                    <ExpirationTimeTransitionSettings
                        setTransitionProp={setTransitionProp}
                        transition={transition as ExpirationTimeTransition}
                    />
                )}

                <Box>
                    <InputGroup>
                        <ColorPicker
                            color={transition.color}
                            onColorChanged={(color: string) => {
                                setTransitionProp('color', color);
                            }}
                        />
                        <DrawerInput
                            ml={1}
                            value={transition.label}
                            onChange={(e) => setTransitionProp('label', e.target.value)}
                            placeholder="Label"
                        />
                    </InputGroup>
                </Box>
            </Stack>
        </Box>
    );
}

type StepDrawerTransitionsAccordionItemProps = {
    stepId: string;
};
export function StepDrawerTransitionsAccordionItem({ stepId }: StepDrawerTransitionsAccordionItemProps) {
    const { pipeline, updateStep } = usePipelineStore((state) => state);
    const step = pipeline.steps[stepId] as PipelineStep;

    const addNewTransition = useCallback(() => {
        let transitions = JSON.parse(JSON.stringify(step.transitions)) as Transition[];
        transitions.push({
            targetStepId: '',
            color: 'black',
            label: '',
            condition: 'NONE',
        });
        updateStep(stepId, 'transitions', transitions);
    }, [step, updateStep]);

    const reorderTransition = useCallback(
        (fromIndex: number, toIndex: number) => {
            const transitions = JSON.parse(JSON.stringify(step.transitions)) as Transition[];
            const [transition] = transitions.splice(fromIndex, 1);
            transitions.splice(toIndex, 0, transition);
            updateStep(stepId, 'transitions', transitions);
        },
        [step, stepId, updateStep],
    );

    if (!step) return null;
    return (
        <Stack margin="auto">
            {step.transitions.map((transition, transitionIndex) => (
                <TransitionSettings
                    key={transitionIndex}
                    stepId={stepId}
                    transitionIndex={transitionIndex}
                    onReorderTransition={reorderTransition}
                />
            ))}
            <Button
                backgroundColor="white"
                fontSize={'sm'}
                leftIcon={<AddIcon />}
                _hover={{
                    backgroundColor: 'white',
                    color: 'black',
                }}
                _focus={{ backgroundColor: 'white' }}
                _active={{ backgroundColor: 'white' }}
                color={'grey'}
                onClick={addNewTransition}
            >
                Add transition
            </Button>
        </Stack>
    );
}
